import { motion } from "framer-motion";
import { HOUSE } from "@/constants/testIds";

const MOMENTS = [
  { place: "A hotel", moment: "You check in. You set your bag on the floor." },
  { place: "A restroom", moment: "The counter is wet. Your bag has nowhere to go." },
  { place: "A spa", moment: "You undress. Your belongings pile onto a bench." },
  { place: "A fitting room", moment: "The hook is broken. Your coat and bag share a stool." },
  { place: "An airport", moment: "You reach for your phone. The floor becomes a table." },
  { place: "An exam room", moment: "The chair is taken by charts. You hold everything on your lap." },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Problem = () => {
  return (
    <section
      id="problem"
      data-testid={HOUSE.sectionProblem}
      className="relative bg-[var(--black)] py-28 md:py-40 border-t border-[var(--border-dark)]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.p
          {...fadeUp}
          className="font-sans text-[11px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-10"
        >
          The Overlooked Moment
        </motion.p>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="font-serif text-[var(--ivory)] text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight font-light max-w-4xl"
        >
          Where do you put
          <br />
          <span className="italic text-[var(--stone)]">
            the things you&apos;re carrying?
          </span>
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.16 }}
          className="mt-12 max-w-2xl font-sans text-[var(--stone)] text-lg leading-relaxed"
        >
          Beautiful spaces are designed with extraordinary attention to
          architecture, lighting, materials, and light. Yet the simple question
          of where to place what you carry is often left unanswered.
        </motion.p>

        {/* Moments — editorial two-column list */}
        <div className="mt-24 grid md:grid-cols-2 gap-x-16 gap-y-14">
          {MOMENTS.map((m, i) => (
            <motion.div
              key={m.place}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.8,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group border-t border-[var(--border-dark)] pt-6"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-serif text-[var(--ivory)] text-2xl md:text-3xl tracking-tight">
                  {m.place}.
                </p>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-[var(--muted-dark)] shrink-0">
                  0{i + 1}
                </span>
              </div>
              <p className="mt-4 font-sans text-[var(--stone)]/85 leading-relaxed">
                {m.moment}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-24 font-serif italic text-[var(--gold-light)] text-2xl md:text-3xl text-center max-w-3xl mx-auto"
        >
          The question remains — where do you set it down?
        </motion.p>
      </div>
    </section>
  );
};
